/* Created by Interactive 63 - https://interactive63.com */
/**
 * Builds the data the runtime needs to morph a shape: a template describing the subpaths, and
 * for each keyframe a flat array of numbers. Normalisation is the expensive step and shapes
 * change far less often than the rest of a project, so results are cached by their inputs.
 */
import { KEYWORD_BEZIERS } from '../easing/cubic-bezier';
import { evaluateEasing } from '../easing/evaluate';
import { pointsForEasing } from '../easing/linear-fn';
import type { RuntimeEasing } from '../easing/runtime-evaluate';
import type { Easing } from '../easing/types';
import type { MorphTemplate } from './format';
import { prepareFrames, type Alignment } from './match';
import { preparePinnedFrames, type Pin } from './pins';
import { toCubicSubpaths, type Subpath } from './normalise';

export interface MorphFrameInput {
  /** Milliseconds from the start of the scene. */
  time: number;
  d: string;
  easing: Easing;
  align?: Alignment;
  /** Pinned points against the previous shape. */
  pins?: readonly Pin[];
  /** Which part of the previous shape each part of this one grows from. */
  parts?: readonly (number | null)[];
}

export interface MorphFrame {
  time: number;
  easing: RuntimeEasing;
  values: number[];
}

export interface MorphTrackData {
  elementId: string;
  /** The class the runtime finds the path by. */
  className: string;
  /** Present for tracks that play once, so the player holds their end pose in later iterations. */
  once?: true;
  template: MorphTemplate;
  frames: MorphFrame[];
}

export type MorphBuildResult =
  | {
      ok: true;
      data: MorphTrackData;
      easings: Easing[];
      segments: number;
      /** Pins or parts that could not be honoured, in plain language. */
      problems: string[];
    }
  | { ok: false; message: string };

export interface MorphBuildInput {
  elementId: string;
  frames: readonly MorphFrameInput[];
  /** Whether the shape uses the evenodd fill rule, which allows reversing multi-part shapes. */
  evenOdd: boolean;
  decimals: number;
  once?: boolean;
}

interface Prepared {
  template: MorphTemplate;
  values: number[][];
  segments: number;
  problems: string[];
}

const cache = new Map<string, Prepared | string>();
const CACHE_LIMIT = 40;

/** Samples a stepped easing as points, with a vertical jump at each boundary. */
function stepPoints(easing: Easing): [number, number][] {
  const edge = 1e-9;
  const boundaries = easing.type === 'steps' ? easing.count : 1;
  const points: [number, number][] = [[0, evaluateEasing(easing, 0)]];
  for (let i = 1; i < boundaries; i += 1) {
    const b = i / boundaries;
    points.push([b, evaluateEasing(easing, b - edge)], [b, evaluateEasing(easing, b)]);
  }
  points.push([1, evaluateEasing(easing, 1 - edge)], [1, evaluateEasing(easing, 1)]);
  return points;
}

/**
 * Converts an easing into the three forms the runtime understands: linear, cubic and points.
 * Keywords become cubics; steps, holds and the physical easings become sampled points.
 */
export function toRuntimeEasing(easing: Easing): RuntimeEasing {
  switch (easing.type) {
    case 'linear':
      return easing;
    case 'cubic':
      return easing;
    case 'keyword': {
      const [x1, y1, x2, y2] = KEYWORD_BEZIERS[easing.name];
      return { type: 'cubic', x1, y1, x2, y2 };
    }
    case 'steps':
    case 'hold':
      return { type: 'points', points: stepPoints(easing) };
    case 'spring':
    case 'bounce':
    case 'elastic':
      return { type: 'points', points: pointsForEasing(easing) };
  }
}

/** Rounds to a number of decimals. */
function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Flattens a subpath into start x, start y, then six numbers per segment. */
function flatten(subpaths: readonly Subpath[], decimals: number): number[] {
  const out: number[] = [];
  for (const path of subpaths) {
    out.push(round(path.start[0], decimals), round(path.start[1], decimals));
    for (const seg of path.segs) for (const value of seg) out.push(round(value, decimals));
  }
  return out;
}

/** The decimals to keep: more for small drawings, where rounding would show. */
export function decimalsFor(viewBox: { width: number; height: number }): number {
  return Math.max(viewBox.width, viewBox.height) < 50 ? 3 : 2;
}

/** Normalises every frame, or returns the first parse error. */
function prepare(input: MorphBuildInput): Prepared | string {
  const key = JSON.stringify([
    input.frames.map((f) => [f.d, f.align, f.pins, f.parts]),
    input.evenOdd,
    input.decimals,
  ]);
  const hit = cache.get(key);
  if (hit !== undefined) return hit;

  const parsed: Subpath[][] = [];
  let result: Prepared | string | undefined;
  for (const frame of input.frames) {
    const subpaths = toCubicSubpaths(frame.d);
    if (!subpaths.ok) {
      result = subpaths.message;
      break;
    }
    parsed.push(subpaths.subpaths);
  }
  if (result === undefined) {
    const options = { allowReverse: input.evenOdd };
    const aligns = input.frames.map((f) => f.align);
    const pinned = input.frames.some((f) => (f.pins?.length ?? 0) > 0 || f.parts !== undefined);
    const outcome = pinned
      ? preparePinnedFrames(parsed, options, aligns, input.frames)
      : { frames: prepareFrames(parsed, options, aligns), problems: [] };
    const prepared = outcome.frames;
    const first = prepared[0] ?? [];
    result = {
      template: { subpaths: first.map((p) => ({ segments: p.segs.length, closed: p.closed })) },
      values: prepared.map((frame) => flatten(frame, input.decimals)),
      segments: first.reduce((sum, p) => sum + p.segs.length, 0),
      problems: outcome.problems,
    };
  }
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value as string);
  cache.set(key, result);
  return result;
}

/** Builds the runtime data for a morph track from its (already expanded) keyframes. */
export function buildMorphTrack(input: MorphBuildInput): MorphBuildResult {
  if (input.frames.length === 0) return { ok: false, message: 'There are no shapes to morph.' };
  const prepared = prepare(input);
  if (typeof prepared === 'string') return { ok: false, message: prepared };
  return {
    ok: true,
    segments: prepared.segments,
    problems: prepared.problems,
    easings: input.frames.map((f) => f.easing),
    data: {
      elementId: input.elementId,
      className: `st-m${input.elementId.slice(1)}`,
      ...(input.once ? { once: true as const } : {}),
      template: prepared.template,
      frames: input.frames.map((frame, i) => ({
        time: frame.time,
        easing: toRuntimeEasing(frame.easing),
        values: prepared.values[i] ?? [],
      })),
    },
  };
}
